import { Fragment, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ChevronDown, ChevronRight, Loader2, RefreshCw, Search, X } from "lucide-react";
import {
  forgetSystemHost,
  getSystemHistory,
  getSystems,
  type SystemHealth,
  type SystemHostState,
  type SystemRow,
  type SystemRun,
} from "../../services/SystemsService";

// Admin → Monitor → Systems: a pulse board. Problems first as cards with their
// error output, then every system as a tile with a 24-hour strip of its runs
// (one bar per hour: green ok, red failed, amber some hosts failed). Cron jobs
// report through cmd/jobrun, services through keep-alives and per-cycle
// results (server pkg/systemruns).

const CARD = "bg-white dark:bg-dark-800 rounded-xl border";
const TH = "px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-dark-400 text-left whitespace-nowrap";
const TD = "px-3 py-1.5 whitespace-nowrap";
const MUTED = "text-gray-500 dark:text-dark-300";
const MONO = "font-mono tabular-nums";

const HEALTH: Record<SystemHealth, { label: string; dot: string; text: string; border: string; bar: string }> = {
  failed: { label: "Failed", dot: "bg-red-500", text: "text-red-700 dark:text-red-400", border: "border-red-200 dark:border-red-900/70", bar: "bg-red-500" },
  late: { label: "Late", dot: "bg-amber-500", text: "text-amber-700 dark:text-amber-400", border: "border-amber-200 dark:border-amber-900/70", bar: "bg-amber-500" },
  partial: { label: "Some hosts", dot: "bg-amber-500", text: "text-amber-700 dark:text-amber-400", border: "border-amber-200 dark:border-amber-900/70", bar: "bg-amber-500" },
  running: { label: "Running", dot: "bg-sky-500", text: "text-sky-700 dark:text-sky-400", border: "border-gray-200 dark:border-dark-700", bar: "bg-sky-500" },
  ok: { label: "OK", dot: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-400", border: "border-gray-200 dark:border-dark-700", bar: "bg-emerald-500" },
  unknown: { label: "No reports", dot: "bg-gray-300 dark:bg-dark-500", text: "text-gray-400 dark:text-dark-400", border: "border-gray-200 dark:border-dark-700", bar: "bg-gray-200" },
  off: { label: "Off", dot: "bg-gray-200 dark:bg-dark-600", text: "text-gray-400 dark:text-dark-400", border: "border-gray-200 dark:border-dark-700", bar: "bg-gray-200" },
};

const ATTENTION: SystemHealth[] = ["failed", "late", "partial"];

const ago = (ms?: number) => {
  if (!ms) return "—";
  const minutes = Math.round(Math.max(0, Date.now() - ms) / 60000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 48 * 60) return `${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ""}`;
  return `${Math.round(minutes / 1440)}d`;
};
const stamp = (ms?: number) => (ms ? new Date(ms).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const duration = (ms?: number) => {
  if (!ms || ms < 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  return m % 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m / 60}h`;
};
// Services: last keep-alive. Cron jobs: last real run, else the last idle
// tick (queue jobs like the feed repair queue mostly find nothing to do).
const lastActivity = (row: SystemRow) => (row.kind === "service" ? row.lastSeenAt : row.lastRunAt || row.lastSeenAt);

function Dot({ health, className = "" }: { health: SystemHealth; className?: string }) {
  return <span className={`h-2 w-2 shrink-0 rounded-full ${HEALTH[health]?.dot ?? "bg-gray-300"} ${className}`} />;
}

function Status({ health }: { health: SystemHealth }) {
  const s = HEALTH[health] ?? HEALTH.unknown;
  return <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${s.text}`}><Dot health={health} />{s.label}</span>;
}

function Ago({ ms, empty = "—" }: { ms?: number; empty?: string }) {
  if (!ms) return <span className="text-gray-300 dark:text-dark-500">{empty}</span>;
  return <span title={new Date(ms).toLocaleString()} className="tabular-nums">{ago(ms)}</span>;
}

// 24 bars, one per hour, oldest on the left; the current hour pulses while running.
function HourStrip({ row }: { row: SystemRow }) {
  const hours = row.hours?.length === 24 ? row.hours : Array<string>(24).fill("");
  const now = new Date();
  return (
    <div className="flex h-3.5 items-end gap-[2px]" aria-hidden="true">
      {hours.map((h, i) => {
        const current = i === 23;
        const running = current && row.health === "running";
        const cls = running ? "h-full bg-sky-500 animate-pulse"
          : h === "failed" ? "h-full bg-red-500"
            : h === "partial" ? "h-full bg-amber-500"
              : h === "ok" ? "h-2.5 bg-emerald-500"
                : "h-1 bg-gray-200 dark:bg-dark-600";
        const hourAgo = 23 - i;
        const label = new Date(now.getTime() - hourAgo * 3600_000).toLocaleTimeString(undefined, { hour: "numeric" });
        return <span key={i} title={`${label}: ${running ? "running" : h || "no run"}`} className={`flex-1 rounded-[2px] ${cls}`} />;
      })}
    </div>
  );
}

function Chip({ label, value, active, tone, onClick }: { label: string; value: number; active: boolean; tone?: "bad"; onClick: () => void }) {
  const bad = tone === "bad" && value > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold ring-1 transition-colors ${active
        ? "bg-gray-900 text-white ring-gray-900 dark:bg-white dark:text-dark-900 dark:ring-white"
        : bad
          ? "bg-red-50 text-red-700 ring-red-200 hover:bg-red-100 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900"
          : "bg-white text-gray-600 ring-gray-200 hover:bg-gray-50 dark:bg-dark-800 dark:text-dark-200 dark:ring-dark-700 dark:hover:bg-dark-700"
        }`}
    >
      {label}
      <span className="tabular-nums opacity-70">{value}</span>
    </button>
  );
}

function ErrorTail({ lines, max }: { lines?: string[]; max?: number }) {
  if (!lines?.length) return null;
  const shown = max ? lines.slice(-max) : lines;
  return (
    <pre className="max-h-48 overflow-auto rounded-md bg-gray-900 px-2.5 py-2 font-mono text-[11px] leading-snug text-gray-100 whitespace-pre-wrap break-all dark:bg-black/40">
      {shown.join("\n")}
    </pre>
  );
}

// ── Problem cards ────────────────────────────────────────────────

function AlertCard({ row, onOpen }: { row: SystemRow; onOpen: () => void }) {
  const s = HEALTH[row.health];
  const tint = row.health === "failed" ? "bg-red-50/60 dark:bg-red-950/20" : "bg-amber-50/60 dark:bg-amber-950/20";
  return (
    <button type="button" onClick={onOpen} className={`${CARD} ${s.border} ${tint} flex min-w-0 flex-col gap-2 p-3.5 text-left transition-shadow hover:shadow-md`}>
      <div className="flex w-full items-center gap-2">
        <Dot health={row.health} />
        <span className={`text-xs font-semibold ${s.text}`}>
          {row.fleet && row.hostsTotal ? `${row.hostsOk ?? 0}/${row.hostsTotal} hosts OK` : s.label}
        </span>
        <span className={`ml-auto text-xs ${MONO} ${MUTED}`} title={stamp(lastActivity(row))}>{ago(lastActivity(row))}</span>
      </div>
      <span className="truncate text-[15px] font-semibold text-gray-900 dark:text-white">{row.name}</span>
      {row.reason && <span className={`text-xs ${s.text}`}>{row.reason}</span>}
      {row.lastError?.length ? <ErrorTail lines={row.lastError} max={3} /> : null}
    </button>
  );
}

// ── Tiles ────────────────────────────────────────────────────────

function Tile({ row, onOpen }: { row: SystemRow; onOpen: () => void }) {
  const s = HEALTH[row.health];
  const quiet = row.health === "unknown" || row.health === "off";
  const meta = row.fleet && row.hostsTotal ? `${row.where} · ${row.hostsOk ?? 0}/${row.hostsTotal}` : row.where;
  return (
    <button
      type="button"
      onClick={onOpen}
      title={row.reason || row.description || row.name}
      className={`${CARD} ${s.border} flex min-w-0 flex-col gap-2 px-3 py-2.5 text-left transition-colors hover:border-gray-300 dark:hover:border-dark-500 ${quiet ? "opacity-70" : ""}`}
    >
      <div className="flex w-full min-w-0 items-center gap-2">
        <Dot health={row.health} />
        <span className="truncate text-[13px] font-semibold text-gray-900 dark:text-white">{row.name}</span>
        <span className={`ml-auto shrink-0 text-xs ${MONO} ${row.health === "running" ? "text-sky-700 dark:text-sky-400" : ATTENTION.includes(row.health) ? s.text : MUTED}`}>
          {row.health === "running" ? "running" : row.off ? "off" : lastActivity(row) ? ago(lastActivity(row)) : "never"}
        </span>
      </div>
      <HourStrip row={row} />
      <div className={`flex w-full min-w-0 justify-between gap-2 text-[11px] ${MONO} text-gray-400 dark:text-dark-400`}>
        <span className="truncate">{row.off ? "switched off" : row.health === "unknown" ? "not reporting yet" : meta}</span>
        <span className="shrink-0">{row.kind === "service" ? "service" : duration(row.lastDurationMs)}</span>
      </div>
    </button>
  );
}

// ── Detail drawer ────────────────────────────────────────────────

function HostsTable({ row, onForget }: { row: SystemRow; onForget: (host: SystemHostState) => void }) {
  const hosts = row.hosts ?? [];
  if (hosts.length === 0) return null;
  return (
    <table className="w-full text-xs">
      <thead>
        <tr><th className={TH}>Host</th><th className={TH}>Status</th><th className={TH}>Last run</th><th className={TH}>Last OK</th><th className={TH}>Took</th><th className={TH}></th></tr>
      </thead>
      <tbody>
        {hosts.map((h) => (
          <tr key={h.host} className="border-t border-gray-100 dark:border-dark-700/60">
            <td className={`${TD} font-mono`}>{h.host}</td>
            <td className={TD}>
              <Status health={h.health} />
              {h.failStreak && h.failStreak > 1 ? <span className="ml-1 text-red-600 dark:text-red-400">×{h.failStreak}</span> : null}
            </td>
            <td className={TD}><Ago ms={h.lastStartedAt} /></td>
            <td className={TD}><Ago ms={h.lastSuccessAt} empty="never" /></td>
            <td className={`${TD} tabular-nums`}>{h.health === "running" ? `running ${duration(Date.now() - (h.runningSince ?? Date.now()))}` : duration(h.lastDurationMs)}</td>
            <td className={`${TD} w-6 text-right`}>
              <button
                type="button"
                aria-label={`Remove ${h.host}`}
                title="Remove this host (e.g. a retired crawler). It reappears if it reports again."
                onClick={() => onForget(h)}
                className="rounded p-0.5 text-gray-300 hover:bg-gray-100 hover:text-gray-700 dark:text-dark-500 dark:hover:bg-dark-700 dark:hover:text-dark-100"
              >
                <X className="h-3 w-3" />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function History({ job }: { job: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-system-history", job],
    queryFn: () => getSystemHistory(job, 40),
    refetchInterval: 60_000,
  });
  const [openRun, setOpenRun] = useState<number | null>(null);
  if (isLoading) return <p className={`px-3 py-2 text-xs ${MUTED}`}><Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" />Loading…</p>;
  if (error) return <p className="px-3 py-2 text-xs text-red-600">Could not load history.</p>;
  const runs: SystemRun[] = data ?? [];
  if (runs.length === 0) return <p className={`px-3 py-2 text-xs ${MUTED}`}>No runs recorded yet.</p>;
  return (
    <table className="w-full text-xs">
      <thead>
        <tr><th className={TH}>Started</th><th className={TH}>Host</th><th className={TH}>Result</th><th className={TH}>Exit</th><th className={TH}>Took</th></tr>
      </thead>
      <tbody>
        {runs.map((r, i) => {
          const health: SystemHealth = r.status === "failed" ? "failed" : r.status === "running" ? "running" : "ok";
          return (
            <Fragment key={`${r.startedAt}-${r.host}-${i}`}>
              <tr
                className={`border-t border-gray-100 dark:border-dark-700/60 ${r.tail?.length ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700/40" : ""}`}
                onClick={() => r.tail?.length && setOpenRun(openRun === i ? null : i)}
              >
                <td className={TD} title={new Date(r.startedAt).toLocaleString()}>{stamp(r.startedAt)}</td>
                <td className={`${TD} font-mono`}>{r.host}</td>
                <td className={TD}>
                  <Status health={health} />
                  {r.tail?.length ? <span className="ml-1.5 text-red-600 dark:text-red-400">output ▸</span> : null}
                </td>
                <td className={`${TD} tabular-nums`}>{r.status === "running" ? "—" : r.exitCode}</td>
                <td className={`${TD} tabular-nums`}>{duration(r.durationMs)}</td>
              </tr>
              {openRun === i && r.tail?.length ? (
                <tr><td colSpan={5} className="px-3 pb-2"><ErrorTail lines={r.tail} /></td></tr>
              ) : null}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function DetailDrawer({ row, onClose, onForget }: { row: SystemRow; onClose: () => void; onForget: (host: SystemHostState) => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const showHosts = (row.hosts?.length ?? 0) > 1 || row.fleet;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={row.name}>
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-gray-900/30 dark:bg-black/50" />
      <div className="relative flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-gray-50 shadow-2xl dark:bg-dark-900">
        <div className="sticky top-0 z-10 flex items-start gap-3 border-b border-gray-200 bg-white px-5 py-4 dark:border-dark-700 dark:bg-dark-800">
          <div className="min-w-0">
            <Status health={row.health} />
            <h3 className="mt-1 truncate text-lg font-bold text-gray-900 dark:text-white">{row.name}</h3>
            <p className={`text-xs ${MUTED}`}><span className="font-mono">{row.id}</span> · {row.where} · {row.schedule}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="ml-auto rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-dark-700 dark:hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 p-5">
          <div className={`${CARD} border-gray-200 p-3.5 dark:border-dark-700`}>
            <div className="mb-2 flex justify-between text-[11px] text-gray-400 dark:text-dark-400"><span>24 hours ago</span><span>now</span></div>
            <HourStrip row={row} />
            <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-4">
              <div><dt className={MUTED}>Last run</dt><dd className="font-semibold text-gray-900 dark:text-white"><Ago ms={lastActivity(row)} empty="never" /></dd></div>
              <div><dt className={MUTED}>Last OK</dt><dd className="font-semibold text-gray-900 dark:text-white"><Ago ms={row.lastSuccessAt} empty="never" /></dd></div>
              <div><dt className={MUTED}>Took</dt><dd className="font-semibold text-gray-900 dark:text-white">{row.kind === "service" ? "—" : duration(row.lastDurationMs)}</dd></div>
              <div><dt className={MUTED}>Late after</dt><dd className="font-semibold text-gray-900 dark:text-white">{row.staleAfterSec ? duration(row.staleAfterSec * 1000) : "—"}</dd></div>
            </dl>
            {(row.reason || row.description || row.signalSource) && (
              <p className={`mt-3 text-xs ${MUTED}`}>
                {[row.reason, row.description, row.signalSource && `Also read from ${row.signalSource}`].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          {row.lastError?.length ? (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-red-700 dark:text-red-400">Output from the last failed run</p>
              <ErrorTail lines={row.lastError} />
            </div>
          ) : null}
          {showHosts && (
            <div className={`${CARD} overflow-x-auto border-gray-200 dark:border-dark-700`}>
              <p className="px-3 pt-2.5 text-sm font-semibold text-gray-900 dark:text-white">Hosts</p>
              <HostsTable row={row} onForget={onForget} />
            </div>
          )}
          <div className={`${CARD} overflow-x-auto border-gray-200 dark:border-dark-700`}>
            <p className="px-3 pt-2.5 text-sm font-semibold text-gray-900 dark:text-white">Recent runs</p>
            <History job={row.id} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tab ──────────────────────────────────────────────────────────

function groupSummary(rows: SystemRow[]) {
  const n = (h: SystemHealth[]) => rows.filter((r) => h.includes(r.health)).length;
  const parts = [
    n(["ok"]) && `${n(["ok"])} ok`,
    n(["running"]) && `${n(["running"])} running`,
    n(["partial"]) && `${n(["partial"])} partial`,
    n(["late"]) && `${n(["late"])} late`,
    n(["failed"]) && `${n(["failed"])} failed`,
    n(["unknown"]) && `${n(["unknown"])} not reporting`,
  ].filter(Boolean);
  return parts.join(" · ");
}

export default function SystemsTab() {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "attention" | "unknown" | "running">("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["admin-systems"],
    queryFn: getSystems,
    refetchInterval: 30_000,
  });

  const systems = useMemo(() => data?.systems ?? [], [data]);
  const counts = useMemo(() => {
    const c = { total: systems.length, running: 0, attention: 0, unknown: 0 };
    for (const s of systems) {
      if (s.health === "running") c.running++;
      else if (ATTENTION.includes(s.health)) c.attention++;
      else if (s.health === "unknown") c.unknown++;
    }
    return c;
  }, [systems]);

  const q = query.trim().toLowerCase();
  const visible = useMemo(() => systems.filter((s) => {
    if (filter === "attention" && !ATTENTION.includes(s.health)) return false;
    if (filter === "unknown" && s.health !== "unknown") return false;
    if (filter === "running" && s.health !== "running") return false;
    if (!q) return true;
    return [s.name, s.id, s.where, s.description ?? "", s.group, ...(s.hosts ?? []).map((h) => h.host)].some((v) => v.toLowerCase().includes(q));
  }), [systems, filter, q]);

  const alerts = useMemo(() => visible.filter((s) => ATTENTION.includes(s.health))
    .sort((a, b) => (a.health === "failed" ? 0 : 1) - (b.health === "failed" ? 0 : 1)), [visible]);

  const groups = useMemo(() => {
    const order = data?.groups ?? [];
    const byGroup = new Map<string, SystemRow[]>();
    for (const s of visible) {
      if (!byGroup.has(s.group)) byGroup.set(s.group, []);
      byGroup.get(s.group)!.push(s);
    }
    const names = [...order.filter((g) => byGroup.has(g)), ...[...byGroup.keys()].filter((g) => !order.includes(g))];
    return names.map((name) => ({ name, rows: byGroup.get(name)! }));
  }, [visible, data]);

  const openRow = openId ? systems.find((s) => s.id === openId) ?? null : null;

  const handleForget = async (host: SystemHostState) => {
    if (!window.confirm(`Remove ${host.host} from "${host.job}"? It comes back if it reports again.`)) return;
    await forgetSystemHost(host.job, host.host);
    await queryClient.invalidateQueries({ queryKey: ["admin-systems"] });
  };

  if (isLoading) {
    return <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-red-600" /></div>;
  }
  if (error || !data) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
        <AlertCircle className="h-4 w-4" /> Could not load systems: {(error as Error)?.message ?? "no data"}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="mr-2 text-xl font-bold text-gray-900 dark:text-white">Systems</h2>
        <Chip label="All" value={counts.total} active={filter === "all"} onClick={() => setFilter("all")} />
        <Chip label="Needs attention" value={counts.attention} tone="bad" active={filter === "attention"} onClick={() => setFilter("attention")} />
        <Chip label="Running" value={counts.running} active={filter === "running"} onClick={() => setFilter("running")} />
        <Chip label="No reports" value={counts.unknown} active={filter === "unknown"} onClick={() => setFilter("unknown")} />
        <div className="ml-auto flex w-full items-center gap-2 sm:w-auto">
        <div className="relative min-w-0 flex-1 sm:w-64 sm:flex-none">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search systems or hosts"
            aria-label="Search systems or hosts"
            className="h-8 w-full rounded-lg border border-gray-200 bg-white pl-8 pr-2 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500/40 dark:border-dark-700 dark:bg-dark-800 dark:text-white"
          />
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          aria-label="Refresh"
          title={`Updated ${stamp(data.generatedAt)} · refreshes every 30s`}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-dark-700 dark:bg-dark-800 dark:text-dark-200 dark:hover:bg-dark-700"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
        </button>
        </div>
      </div>

      {alerts.length > 0 && (
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {alerts.map((row) => <AlertCard key={row.id} row={row} onOpen={() => setOpenId(row.id)} />)}
        </section>
      )}

      {groups.length === 0 && <p className={`py-10 text-center text-sm ${MUTED}`}>Nothing matches.</p>}

      {groups.map((g) => {
        const bad = g.rows.filter((r) => ATTENTION.includes(r.health)).length;
        // Long healthy groups (the ~55 online crawlers) start folded.
        const isCollapsed = collapsed[g.name] ?? (g.rows.length > 16 && bad === 0 && !q && filter === "all");
        return (
          <section key={g.name} className="space-y-2.5">
            <button
              type="button"
              onClick={() => setCollapsed({ ...collapsed, [g.name]: !isCollapsed })}
              className="flex items-baseline gap-2.5 text-left"
            >
              {isCollapsed
                ? <ChevronRight className="h-3.5 w-3.5 self-center text-gray-400" />
                : <ChevronDown className="h-3.5 w-3.5 self-center text-gray-400" />}
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-dark-200">{g.name}</h3>
              <span className={`text-xs ${MONO} text-gray-400 dark:text-dark-400`}>{groupSummary(g.rows)}</span>
            </button>
            {!isCollapsed && (
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {g.rows.map((row) => <Tile key={row.id} row={row} onOpen={() => setOpenId(row.id)} />)}
              </div>
            )}
          </section>
        );
      })}

      {openRow && <DetailDrawer row={openRow} onClose={() => setOpenId(null)} onForget={handleForget} />}
    </div>
  );
}
